import { useMemo, useState, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrthographicCamera } from "@react-three/drei";
import * as THREE from "three";
import { useRef } from "react";
import {
  calculateObliqueMatrix,
  applyObliqueProjection,
} from "@/three/utils/projection";
import { DEFAULT_PROJECTION_PARAMS } from "@/three/config/presets";
import { useGUITriangleStore } from "@/stores/guiTriangleStore";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";

/**
 * Oblique 투영을 적용하는 씬 컨테이너
 */
function ObliqueScene({ children }: { children: React.ReactNode }) {
  const groupRef = useRef<THREE.Group>(null);

  useFrame(() => {
    if (!groupRef.current) return;

    const obliqueMatrix = calculateObliqueMatrix(DEFAULT_PROJECTION_PARAMS);
    applyObliqueProjection(groupRef.current, obliqueMatrix);
  });

  return <group ref={groupRef}>{children}</group>;
}

/** 코너 타입 */
export type CornerType = "topLeft" | "topRight" | "bottomLeft" | "bottomRight";

interface OverlayCanvasProps {
  /** 코너 위치 (스토어에서 상태를 읽을 키) */
  corner: CornerType;
  /** 자식 3D 요소 */
  children: React.ReactNode;
  /** 줌 레벨 */
  zoom?: number;
  /** 배경색 (override) */
  backgroundColor?: string;
  /** 배경 투명도 */
  backgroundOpacity?: number;
}

/**
 * 각 코너별 스타일 계산
 */
function getCornerStyles(
  corner: CornerType,
  width: number,
  height: number,
  offsetX: number,
  offsetY: number
): React.CSSProperties {
  const baseStyle: React.CSSProperties = {
    position: "fixed",
    width: `${width}vw`,
    height: `${height}vh`,
    minWidth: "200px",
    minHeight: "300px",
    zIndex: 100,
    pointerEvents: "auto" as const,
  };

  switch (corner) {
    case "topLeft":
      return {
        ...baseStyle,
        left: 0,
        top: 0,
        clipPath: "polygon(0% 0%, 0% 100%, 100% 0%)",
        transform: `translate(${-offsetX}%, ${-offsetY}%)`,
      };
    case "topRight":
      return {
        ...baseStyle,
        right: 0,
        top: 0,
        clipPath: "polygon(0% 0%, 100% 0%, 100% 100%)",
        transform: `translate(${offsetX}%, ${-offsetY}%)`,
      };
    case "bottomLeft":
      return {
        ...baseStyle,
        left: 0,
        bottom: 0,
        clipPath: "polygon(0% 0%, 0% 100%, 100% 100%)",
        transform: `translate(${-offsetX}%, ${offsetY}%)`,
      };
    case "bottomRight":
    default:
      return {
        ...baseStyle,
        right: 0,
        bottom: 0,
        clipPath: "polygon(0% 100%, 100% 0%, 100% 100%)",
        transform: `translate(${offsetX}%, ${offsetY}%)`,
      };
  }
}

/**
 * controlPoint 기반으로 캔버스 크기 계산
 * @param controlPoint 제어점 (0~1)
 * @param aspect 뷰포트 종횡비 (window.innerWidth / window.innerHeight)
 */
function calculateCanvasDimensions(
  controlPoint: number = 0.5,
  aspect: number = 1.77
) {
  const tan30 = Math.tan(30 * (Math.PI / 180));
  const canvasWidth = (1 - controlPoint) * 100;
  // 각도가 30도가 되도록 계산: tan(30) = (height_px / width_px)
  // height_px = canvasHeight * vh, width_px = canvasWidth * vw
  // canvasHeight = canvasWidth * tan30 * (vw / vh) = canvasWidth * tan30 * aspect
  const canvasHeight = canvasWidth * tan30 * aspect;

  return { width: canvasWidth, height: canvasHeight };
}

/**
 * 범용 오버레이 캔버스 컴포넌트
 * 4개 코너 모두 지원, clip-path로 삼각형 마스킹
 *
 * guiTriangleStore에서 controlPoint, animationProgress를 읽어
 * 자동으로 동기화됩니다.
 */
export function OverlayCanvas({
  corner,
  children,
  zoom = 60,
  backgroundColor,
}: OverlayCanvasProps) {
  // 스토어에서 해당 코너 상태 읽기
  const cornerState = useGUITriangleStore((state) => state.corners[corner]);
  const { enabled, controlPoint, animationProgress, color } = cornerState;

  // 배경색: props가 있으면 사용, 없으면 스토어 color 사용
  const bgColor = backgroundColor ?? color;

  // 사라지는 애니메이션 완료 시까지 렌더링 유지
  const [shouldRender, setShouldRender] = useState(enabled);

  // children 캐시 - enabled일 때만 업데이트
  const cachedChildrenRef = useRef<React.ReactNode>(children);

  // 뷰포트 종횡비 관리
  const [aspect, setAspect] = useState(
    typeof window !== "undefined" ? window.innerWidth / window.innerHeight : 1.77
  );

  useEffect(() => {
    const handleResize = () =>
      setAspect(window.innerWidth / window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // enabled이고 children이 있을 때만 캐시 업데이트
  useEffect(() => {
    if (enabled && children) {
      cachedChildrenRef.current = children;
    }
  }, [enabled, children]);

  useEffect(() => {
    if (enabled) {
      // 나타날 때: 즉시 렌더링 시작
      setShouldRender(true);
    }
  }, [enabled]);

  useEffect(() => {
    // 사라질 때: 애니메이션이 거의 완료되면 렌더링 중지
    if (!enabled && animationProgress >= 0.99) {
      setShouldRender(false);
    }
  }, [enabled, animationProgress]);

  const canvasDimensions = useMemo(
    () => calculateCanvasDimensions(controlPoint, aspect),
    [controlPoint, aspect]
  );

  if (!shouldRender) return null;

  const offsetX = animationProgress * 100;
  const offsetY = animationProgress * 100;

  const cornerStyles = getCornerStyles(
    corner,
    canvasDimensions.width,
    canvasDimensions.height,
    offsetX,
    offsetY
  );

  return (
    <div
      style={{
        ...cornerStyles,
        transition:
          animationProgress === 0 || animationProgress === 1
            ? "none"
            : "transform 0.07s ease-out",
      }}
    >
      <Canvas
        gl={{ antialias: true, alpha: true }}
        style={{
          position: "absolute",
          inset: 0,
          background: bgColor,
        }}
      >
        <QueryClientProvider client={queryClient}>
          <OrthographicCamera
            makeDefault
            position={[0, 0, 100]}
            zoom={zoom}
            near={0.1}
            far={1000}
          />

          <ambientLight intensity={0.6} />
          <directionalLight position={[5, 10, 5]} intensity={0.8} />
          <directionalLight position={[-5, 5, -5]} intensity={0.4} />

          {/* enabled일 때는 현재 children, 아닐 때는 캐시된 children 사용 */}
          <ObliqueScene>
            {enabled ? children : cachedChildrenRef.current}
          </ObliqueScene>
        </QueryClientProvider>
      </Canvas>
    </div>
  );
}
